import { FC, ReactNode } from 'react';

import { Link, usePage } from '@inertiajs/react';
import { ArrowRight, Sparkles } from 'lucide-react';

import { InfiniteScrollTrigger } from '../../components/shared/InfiniteScrollTrigger';
import { Button } from '../../components/ui/Button';
import { TooltipProvider } from '../../components/ui/Tooltip';
import { Offer, OfferProperty } from '../../types';
import { cn } from '../../utils';
import { SellerOfferProductCard } from '../Users/SellerOfferProductCard';

interface Props {
    offers: Offer[] | null;
    title?: string;
    description?: string;
    resultsCount?: number;
    nextPage?: number | null;
    loading?: boolean;
    onLoadMore?: () => void;
    viewAllHref?: string;
    viewAllLabel?: string;
    sectionClassName?: string;
    contentClassName?: string;
    largeScreenOfferLimit?: number;
    trailingControl?: ReactNode;
    withDesktopSidebar?: boolean;
}

type PageProps = {
    offer_properties?: OfferProperty[] | null;
};

const getOfferCountText = (count: number) => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return `${count} пропозицій`;
    }

    if (lastDigit === 1) {
        return `${count} пропозиція`;
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return `${count} пропозиції`;
    }

    return `${count} пропозицій`;
};

const getResponsiveOfferVisibilityClass = (
    index: number,
    largeScreenOfferLimit?: number,
) => {
    if (typeof largeScreenOfferLimit !== 'number') {
        return undefined;
    }

    if (index < Math.min(largeScreenOfferLimit, 4)) {
        return undefined;
    }

    if (index === 4 && largeScreenOfferLimit >= 5) {
        return 'md:hidden lg:block';
    }

    if (index === 5 && largeScreenOfferLimit >= 6) {
        return 'md:hidden 2xl:block';
    }

    return 'md:hidden';
};

export const premiumOffersActionButtonClass =
    'border-amber-300/80 bg-white/90 text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-200 dark:hover:bg-amber-400/20 dark:hover:text-amber-100';

export const PremiumOffersSection: FC<Props> = ({
    offers,
    title = 'Преміум пропозиції',
    description = 'Добірка найпомітніших пропозицій продавців з активним преміумом.',
    resultsCount,
    nextPage,
    loading = false,
    onLoadMore,
    viewAllHref,
    viewAllLabel = 'Усі преміум пропозиції',
    sectionClassName,
    contentClassName,
    largeScreenOfferLimit,
    trailingControl,
    withDesktopSidebar = false,
}) => {
    const premiumOffers = offers ?? [];
    const { offer_properties: rawOfferProperties } = usePage<PageProps>().props;
    const offerProperties = rawOfferProperties ?? [];

    if (premiumOffers.length === 0 && typeof resultsCount !== 'number') {
        return null;
    }

    return (
        <section
            className={cn(
                'border-b border-border/60 bg-background py-6 pb-8',
                sectionClassName,
            )}
        >
            <div
                className={cn(
                    'container px-4 sm:px-6 lg:px-8',
                    contentClassName,
                )}
            >
                <TooltipProvider delayDuration={300}>
                    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div className="space-y-2">
                            <div className="space-y-1">
                                <h2 className="flex items-center gap-2 text-lg font-bold md:text-xl">
                                    <Sparkles className="h-5 w-5 text-amber-600" />
                                    {title}
                                </h2>
                                <p className="max-w-2xl text-sm text-muted-foreground">
                                    {description}
                                    {typeof resultsCount === 'number'
                                        ? ` • ${getOfferCountText(resultsCount)}`
                                        : ''}
                                </p>
                            </div>
                        </div>

                        {viewAllHref && (
                            <Button
                                asChild
                                variant="outline"
                                className={cn(
                                    'rounded-xl',
                                    premiumOffersActionButtonClass,
                                )}
                            >
                                <Link href={viewAllHref}>
                                    {viewAllLabel}
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                        )}
                    </div>

                    {premiumOffers.length > 0 ? (
                        <>
                            <div className="relative">
                                <div
                                    className={cn(
                                        'grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-4 lg:grid-cols-5 2xl:grid-cols-6',
                                        withDesktopSidebar &&
                                            'lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
                                    )}
                                >
                                    {premiumOffers.map((offer, index) => {
                                        return (
                                            <div
                                                key={offer.id}
                                                className={getResponsiveOfferVisibilityClass(
                                                    index,
                                                    largeScreenOfferLimit,
                                                )}
                                            >
                                                <SellerOfferProductCard
                                                    offer={offer}
                                                    offerProperties={
                                                        offerProperties
                                                    }
                                                    loading={loading}
                                                    eagerLoadImage={index < 8}
                                                    highlightPremium
                                                    showSellerInfo
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                {trailingControl && (
                                    <div className="absolute top-1/2 -right-3 z-20 -translate-y-1/2 sm:-right-4">
                                        {trailingControl}
                                    </div>
                                )}
                            </div>

                            {nextPage && onLoadMore && (
                                <InfiniteScrollTrigger
                                    className="mt-8"
                                    loading={loading}
                                    onLoadMore={onLoadMore}
                                />
                            )}
                        </>
                    ) : (
                        <div className="rounded-3xl border border-dashed border-amber-300/70 bg-white/70 px-6 py-12 text-center dark:border-amber-400/30 dark:bg-amber-400/10">
                            <h3 className="text-lg font-semibold">
                                Преміум пропозиції поки не знайдено
                            </h3>
                            <p className="mt-2 text-sm text-muted-foreground">
                                Коли продавці активують преміум, вони зʼявляться
                                тут.
                            </p>
                            {viewAllHref && (
                                <Button asChild className="mt-4">
                                    <Link href={viewAllHref}>
                                        Перейти до преміум пропозицій
                                    </Link>
                                </Button>
                            )}
                        </div>
                    )}
                </TooltipProvider>
            </div>
        </section>
    );
};
